import { Text, View } from '@react-pdf/renderer';
import { isEmpty } from 'lodash-es';
import React from 'react';
import {
  Field,
  FieldTypeEnum,
  IClientCurrencySettings,
  InvoiceExtra,
  InvoiceExtraType,
} from 'wisestep-api';
import { activityFormattedDate, getFormatDate } from 'wisestep-base';

import { IInvoicePdf } from '../../types/IInvoicePdf';
import { ClientInvoiceExtraField } from '../InvoiceAmountForm';
import { InvoiceTemplate } from '../InvoiceTemplateForm';

import { AdditionalComments } from './AdditionalComments';
import { InvoicePdfCustomField } from './InvoicePdfCustomField';
import styles from './Styles';

const getInvoiceExtrasPreviewString = (
  invoiceExtra: InvoiceExtra,
  invoiceCurrency?: IClientCurrencySettings,
  invoiceExtraChargeList?: ClientInvoiceExtraField[]
) => {
  const getInvoiceExtraById = (id: string) =>
    invoiceExtraChargeList?.find((invoiceExtraObj) => invoiceExtraObj.invoiceExtra?.id === id) ??
    ({} as { invoiceExtra: { id: string }; name: string; amount: number; percent: number });
  if (!isEmpty(invoiceExtraChargeList)) {
    const invoiceExtrasObj = getInvoiceExtraById(invoiceExtra.id);
    const isAdditive = InvoiceExtraType.ADDITIVE === invoiceExtra.type;
    const operator = isAdditive ? '' : '';
    const percent = invoiceExtrasObj.percent ? invoiceExtrasObj.percent + '%' : undefined;
    if (invoiceExtrasObj.amount) {
      if (!isAdditive) {
        return {
          value: `${operator} (${invoiceCurrency?.symbol} ${invoiceExtrasObj.amount})`,
          percent,
        };
      } else
        return {
          value: `${operator} ${invoiceCurrency?.symbol} ${invoiceExtrasObj.amount}`,
          percent,
        };
    } else return undefined;
  }
};

export const Computation = ({
  invoice,
  additionalComments = true,
  template,
  customFields,
}: {
  invoice: IInvoicePdf;
  additionalComments?: boolean;
  template?: InvoiceTemplate;
  customFields: Field[];
}) => {
  const {
    currency: invoiceCurrency,
    total,
    amountInWord,
    subTotal,
    discount,
    discountToggle,
    totalBeforeTaxation,
    selectedInvoiceTaxFields,
    taxation,
    selectedAdditionalTaxFields,
    adjustment,
  } = invoice;

  return (
    <View style={styles.invoice_body}>
      <View style={styles.half_section}>
        <View>
          {(template?.computationAmountInNumbers ?? true) && (
            <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 16, marginBottom: 10 }}>
              Invoice Total: {(invoiceCurrency?.symbol ?? '') + total}
            </Text>
          )}
          {(template?.computationAmountInWords ?? true) && (
            <Text style={{ fontFamily: 'Helvetica', fontSize: 16, color: '#0070c0' }}>
              {amountInWord}
            </Text>
          )}
        </View>
        <View style={{ marginTop: 24 }}>
          {customFields.map((field) => {
            const value = invoice.customFields?.[field.name];
            const text =
              field.type === FieldTypeEnum.Location && value
                ? [value.city, value.state, value.country].toString() || value.continent
                : field.type === FieldTypeEnum.Date && value
                ? getFormatDate(value.toString())
                : field.type === FieldTypeEnum.DateAndTime && value
                ? activityFormattedDate(value.toString())
                : field.type === FieldTypeEnum.Tags && value
                ? value.join()
                : field.type === FieldTypeEnum.Users
                ? value?.[0]
                : field.type === FieldTypeEnum.Dropdown && field.multiple
                ? value?.join(', ')
                : field.type === FieldTypeEnum.Checkbox && field.options.length === 0
                ? value
                  ? 'Yes'
                  : 'No'
                : value;

            return (
              text && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', margin: 2 }}>
                  {field.type === FieldTypeEnum.ClientSPOC ? (
                    <InvoicePdfCustomField key={field.name} field={field} invoice={invoice} />
                  ) : (
                    <>
                      <Text style={styles.color_text}>{field.label}:</Text>
                      <Text style={styles.text}>{text ?? 'Not Available'}</Text>
                    </>
                  )}
                </View>
              )
            );
          })}
        </View>
        {additionalComments && <AdditionalComments invoice={invoice} />}
      </View>

      <View style={{ width: '40%' }}>
        <View style={styles.line_item_bold}>
          <Text>Sub Total: </Text>
          <Text>{(invoiceCurrency?.symbol ?? '') + subTotal}</Text>
        </View>
        <View style={styles.line_item}>
          {discount && discountToggle && (
            <View>
              <Text>{discount + '%'}</Text>
            </View>
          )}
          <Text>{discount}</Text>
        </View>
        {(template?.computationInvoiceExtras ?? true) &&
          (invoice.invoiceExtras as InvoiceExtra[] | undefined)?.map((invoiceExtra) => {
            const invoiceExtraString = getInvoiceExtrasPreviewString(
              invoiceExtra,
              invoiceCurrency,
              invoice.extras
            );
            return (
              invoiceExtraString?.value && (
                <View style={styles.line_item} key={invoiceExtra.id}>
                  <Text>{invoiceExtra.name}:</Text>
                  {invoiceExtraString?.percent && <Text>{invoiceExtraString?.percent ?? 0}</Text>}
                  <Text>{invoiceExtraString?.value ?? 0}</Text>
                </View>
              )
            );
          })}
        <View style={styles.line_item_bold}>
          <Text>Gross Total:</Text>
          <Text>{(invoiceCurrency?.symbol ?? '') + totalBeforeTaxation}</Text>
        </View>
        {(template?.computationInvoiceTaxation ?? true) && (
          <>
            <View style={styles.line_item}>
              <Text>Taxation:</Text>
              <View>
                {selectedInvoiceTaxFields?.map((taxObj) => (
                  <Text key={taxObj.id}>{`${taxObj.name}-${taxObj.rate}%`}</Text>
                ))}
              </View>
              <Text>{(invoiceCurrency?.symbol ?? '') + taxation}</Text>
            </View>
            <View style={styles.line_item}>
              <Text>Additional Tax:</Text>
              <View>
                {selectedAdditionalTaxFields?.map((taxObj) => (
                  <Text key={taxObj.id}>{`${taxObj.name}-${taxObj.rate}%`}</Text>
                ))}
              </View>
              <Text>-{(invoiceCurrency?.symbol ?? '') + adjustment}</Text>
            </View>
          </>
        )}
        <View style={styles.line_item_bold}>
          <Text>Net Total:</Text>
          <Text>{(invoiceCurrency?.symbol ?? '') + total}</Text>
        </View>
      </View>
    </View>
  );
};
